import React, { createContext, useContext, useEffect, useMemo, useState, ReactNode } from 'react';

type SupportedLanguage = 'tr' | 'en' | 'it' | 'ru';

interface LanguageContextType {
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

interface LanguageProviderProps {
  children: ReactNode;
}

// Minimal, high-impact translations used across visible sections
const translations: Record<SupportedLanguage, Record<string, string>> = {
  tr: {
    // Navigation
    'nav.home': 'Anasayfa',
    'nav.products': 'Ürünler',
    'nav.categories': 'Kategoriler',
    'nav.about': 'Hakkımızda',
    'nav.contact': 'İletişim',
    'nav.services': 'Hizmetler',
    'nav.references': 'Referanslar',

    // Header CTA
    'header.cta': 'İletişim',
    'header.menuToggle': 'Menüyü aç/kapat',

    // Hero
    'hero.title': 'Motor Yenileme Teknolojisinde',
    'hero.highlight': 'Lider',
    'hero.subtitle': 'Motor rektefiye ekipman ve aparatlar üretiminde uzman mühendislik firması.',
    'hero.btn.contact': 'Hemen İletişime Geç',
    'hero.btn.products': 'Ürünlerimizi İncele',
    'hero.scroll': 'Aşağı Kaydır',
    'hero.tagline': 'Motor Yenileme Ekipmanları Uzmanı',

    // About
    'about.subtitle': 'Hakkımızda',
    'about.title': 'Üretimde Modern Teknoloji ve Kalite',
    'about.header.desc': 'ESF Otomotiv, motor rektefiye sektörüne yönelik profesyonel ekipman ve aparatların üretiminde uzmanlaşmış bir mühendislik firmasıdır. 2017 yılında ürün pazarlamasıyla başladığımız faaliyetlerimizi, sektörün talepleri ve edindiğimiz deneyim doğrultusunda 2022 yılında üretim odaklı bir yapıya dönüştürdük.',
    'about.experience': 'Üretimde Modern Teknoloji',
    'about.story': 'Şirket Hikayemiz',
    'about.values': 'Değerlerimiz',
    'about.values.reliability': 'Güvenilirlik',
    'about.values.innovation': 'İnovasyon',
    'about.values.customer': 'Müşteri Odaklılık',
    'about.vision': 'Vizyonumuz',
    'about.mission': 'Misyonumuz',
    'about.timeline': 'Tarihçemiz',

    // References
    'references.subtitle': 'Güvenilir Ortaklarımız',
    'references.title': 'Referanslarımız',
    'references.desc': '25 yıllık deneyimimiz boyunca birlikte çalıştığımız değerli iş ortaklarımız ve onlara sunduğumuz kaliteli hizmetler.',
    'references.since': 'İşbirliği Başlangıcı',
    'references.years': 'Yıl İşbirliği',
    'references.projects': 'Proje',
    'references.ach.partners': '50+ İş Ortağı',
    'references.ach.partners.desc': 'Güvenilir ve uzun soluklu iş birlikleri',
    'references.ach.satisfaction': '%98 Müşteri Memnuniyeti',
    'references.ach.satisfaction.desc': 'Kaliteli hizmet ve güvenilir çözümler',
    'references.ach.nationwide': 'Türkiye Geneli',
    'references.ach.nationwide.desc': 'Ülke çapında hizmet ağı',

    // Contact
    'contact.map': 'Konum Haritası',
    'contact.subtitle': 'İletişim',
    'contact.title': 'Bizimle İletişime Geçin',
    'contact.description': 'Size yardımcı olmaktan mutluluk duyarız. Sorularınız için bizimle iletişime geçin.',
    'contact.form.title': 'İletişim Formu',
    'contact.form.subtitle': 'Formu doldurarak bizimle iletişime geçebilirsiniz',
    'contact.submit': 'Mesaj Gönder',
    'contact.info.title': 'İletişim Bilgileri',
    'contact.info.address': 'Adres',
    'contact.info.phone': 'Telefon',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.email': 'E-posta',
    'contact.info.hours': 'Çalışma Saatleri',
    'contact.map.open': 'Haritada Aç',
    'contact.labels.name': 'Ad Soyad *',
    'contact.labels.email': 'E-posta *',
    'contact.labels.phone': 'Telefon',
    'contact.labels.company': 'Şirket',
    'contact.labels.subject': 'Konu *',
    'contact.labels.message': 'Mesaj *',

    // Products
    'products.subtitle': 'Ürünlerimiz',
    'products.title': 'Geniş Ürün Yelpazemiz',
    'products.desc': 'Motor yenileme teknolojisinde ihtiyacınız olan tüm ekipman ve makinalar',
    'products.viewGallery': 'Ürün Galerisi',
    'products.unit': 'ürün',
    'products.moreCategories': 'kategori daha',
    'products.gallery.image': 'Görsel',
    'products.gallery.detail': 'Detay',
    // Products: Top-level categories
    'products.categories.machines.title': 'Motor Yenileme Makinaları',
    'products.categories.machines.desc': 'Endüstriyel kalitede motor yenileme makinaları',
    'products.categories.spare-parts.title': 'Motor Yenileme Yedek Parçaları',
    'products.categories.spare-parts.desc': 'Orijinal yedek parçalar ve bakım kitleri',
    'products.categories.service-equipment.title': 'Otomotiv Servis Elemanları',
    'products.categories.service-equipment.desc': 'Profesyonel servis araçları ve ekipmanları',
    'products.categories.second-hand.title': 'İkinci El Satılık Ürünler',
    'products.categories.second-hand.desc': 'Test edilmiş ve garantili ikinci el ekipmanlar',

    // Services
    'services.subtitle': 'Hizmetlerimiz',
    'services.title': 'Kapsamlı Hizmet Ağımız',
    'services.desc': 'Müşterilerimize sunduğumuz kaliteli hizmetlerle yanınızdayız',
    'services.items.tech-consulting.title': 'Teknik Danışmanlık',
    'services.items.tech-consulting.desc': 'Uzman ekibimizle 7/24 teknik destek ve danışmanlık hizmeti sunuyoruz.',
    'services.items.fast-delivery.title': 'Hızlı Teslimat',
    'services.items.fast-delivery.desc': 'Türkiye genelinde hızlı ve güvenli teslimat ağımızla siparişlerinizi ulaştırıyoruz.',
    'services.items.installation.title': 'Kurulum ve Montaj',
    'services.items.installation.desc': 'Satın aldığınız makinaların profesyonel kurulum ve montaj hizmetini sağlıyoruz.',
    'services.items.warranty.title': 'Garanti ve Servis',
    'services.items.warranty.desc': 'Tüm ürünlerimiz için kapsamlı garanti ve satış sonrası servis desteği.',
    'services.items.training.title': 'Eğitim Hizmetleri',
    'services.items.training.desc': 'Ekipman kullanımı konusunda personel eğitimi ve sertifikasyon programları.',
    'services.items.custom-solutions.title': 'Özel Çözümler',
    'services.items.custom-solutions.desc': 'İhtiyaçlarınıza özel tasarım ve üretim çözümleri geliştiriyoruz.',

    // Hero stats
    'hero.stats.experience': 'Dönüşüm ve Üretim',
    'hero.stats.happyCustomers': 'Mutlu Müşteri',
    'hero.stats.productVariety': 'Ürün Çeşidi',
    'hero.stats.techSupport': 'Teknik Destek',

    // About extra texts
    'about.experience.desc': '2017’den 2022’ye dönüşüm: CNC makine parkuru ve uzman ekip ile üretimi modern teknolojiyle güçlendirdik; kalite süreçleriyle yurt içi ve global pazarlara üretim yapıyoruz.',
    'about.story.text1': 'Bu dönüşüm kapsamında CNC makine parkurumuzu kurarak, alanında uzman teknik ekibimizi bünyemize kattık ve üretim süreçlerimizi modern teknolojiyle güçlendirdik.',
    'about.story.text2': 'Bugün; honlama aparatları, subap yuvası işleme teknolojileri, motor bloğu işleme, özel rektefiye ekipmanları ve makinalara kadar geniş bir yelpazeyi yüksek hassasiyet ve kalite standartlarında üretiyoruz. Kalite, güvenilirlik, yenilikçilik ve müşteri memnuniyeti temel prensiplerimizdir; ürünlerimiz titiz kalite kontrol süreçlerinden geçerek yurt içi ve uluslararası pazarlara sunulmaktadır. Global pazarda büyürken motor rektefiye sektörünün gelişimine katkı sağlamayı hedefliyoruz.',
    'about.values.reliability.desc': 'Müşterilerimizle kurduğumuz güven ilişkisi bizim en büyük değerimizdir.',
    'about.values.innovation.desc': 'Teknolojik gelişmeleri takip ederek sürekli yenilik ve gelişim sağlarız.',
    'about.values.customer.desc': 'Müşteri ihtiyaçlarını anlayarak en uygun çözümleri sunarız.',
    'about.vision.desc': "Motor yenileme teknolojilerinde Türkiye'nin lider firması olmak ve uluslararası pazarlarda güçlü bir konuma sahip olmak.",
    'about.mission.desc': 'Otomotiv sektörüne yüksek kaliteli motor yenileme ekipmanları ve makinaları sunarak müşterilerimizin başarısına katkıda bulunmak.',
    'about.timeline.2017.title': 'Pazarlama Başlangıcı',
    'about.timeline.2017.desc': 'Motor rektefiye ekipmanları pazarlaması ile faaliyetler başladı.',
    'about.timeline.2022.title': 'Üretime Geçiş',
    'about.timeline.2022.desc': 'Sektör talepleri doğrultusunda üretim odaklı yapıya dönüşüm sağlandı.',
    'about.timeline.today.title': 'Bugün',
    'about.timeline.today.desc': 'CNC parkuru ve kalite süreçleriyle, yurt içi ve global pazarlara üretim yapıyoruz.',

    // About features (cards)
    'about.features.0.title': 'CNC Üretim Altyapısı',
    'about.features.0.desc': 'Modern CNC parkur ve uzman teknik ekip',
    'about.features.1.title': 'Geniş Ürün Yelpazesi',
    'about.features.1.desc': '500+ farklı ürün ve çözüm seçeneği',
    'about.features.2.title': 'Türkiye Geneli Hizmet',
    'about.features.2.desc': 'Hızlı teslimat ve teknik destek ağı',
    'about.features.3.title': 'Kalite Garantisi',
    'about.features.3.desc': 'Tüm ürünlerde uluslararası kalite standartları',

    // Contact errors & success
    'contact.success': 'Mesajınız başarıyla gönderildi. En kısa sürede size dönüş yapacağız.',
    'contact.errors.nameRequired': 'Ad soyad zorunludur',
    'contact.errors.emailRequired': 'E-posta zorunludur',
    'contact.errors.emailInvalid': 'Geçerli bir e-posta adresi giriniz',
    'contact.errors.subjectRequired': 'Konu zorunludur',
    'contact.errors.messageRequired': 'Mesaj zorunludur',
    // Company Working Hours
    'company.hours.weekdays': 'Pazartesi - Cuma: 08:00 - 18:00',
    'company.hours.saturday': 'Cumartesi: 09:00 - 16:00',
    'company.hours.sunday': 'Pazar: Kapalı',
    
    // Footer UI
    'footer.quickLinks': 'Hızlı Linkler',
    'footer.description': 'Motor rektefiye ekipman ve aparatlar üretiminde uzman bir mühendislik firmasıyız. CNC makine parkurumuz ve uzman ekibimizle, honlama aparatları, subap yuvası işleme, motor bloğu işleme ve özel rektefiye ekipmanlarını yüksek hassasiyetle üretiriz. Kalite, güvenilirlik ve müşteri memnuniyeti temel prensiplerimizdir.',
    'footer.copyright': 'Tüm hakları saklıdır.',
    'footer.legal.privacy': 'Gizlilik',
    'footer.legal.terms': 'Koşullar',
    'footer.legal.cookies': 'Çerezler',
    'footer.developer.by': 'Geliştiren',

    // Products - Additional top-level categories (name-based ones)
    'products.categories.motor-yenileme.title': 'Motor Yenileme Makinaları',
    'products.categories.motor-yenileme.description': 'Motor yenileme ve revizyon makinaları',
    'products.categories.test-olcu.title': 'Test ve Ölçü Ekipmanları',
    'products.categories.test-olcu.description': 'Motor test ve ölçüm ekipmanları',
    'products.categories.polisaj-yikama.title': 'Yıkama ve Polisaj Makinaları',
    'products.categories.polisaj-yikama.description': 'Endüstriyel yıkama ve polisaj makinaları',
    'products.categories.subap-taslama.title': 'Subap ve Kater Taşlama',
    'products.categories.subap-taslama.description': 'Subap taşlama ve kater grupları ekipmanları',
    'products.filter.all': 'Tümü',
    'products.categoryGallery': 'Kategori Galerisi',
    'products.search.placeholder': 'Ürün adı veya açıklama ara',
    'sort.recent': 'En yeni',
    'sort.name': 'İsme göre (A-Z)',
    'sort.category': 'Kategoriye göre',
    'view.grid': 'Izgara',
    'view.list': 'Liste',
    'results.label': 'sonuç',
    'common.detail': 'Detay',
    'whatsapp.messageWithProduct': 'Merhaba, {product} hakkında bilgi ve teklif almak istiyorum.',
    'products.empty.title': 'Henüz katalogda ürün yok',
    'products.empty.desc': 'Müşteri temsilcimizle iletişime geçerek ihtiyacınıza uygun ürünü hızlıca listeleyelim.',
    'contact.whatsapp.cta': 'WhatsApp ile İletişim',
    'contact.form.cta': 'İletişim Formu',
    'common.loadMore': 'Daha fazla yükle',
    'ui.copyLinkSuccess': 'Bağlantı kopyalandı',
    'common.close': 'Kapat',
    'common.previous': 'Önceki',
    'common.next': 'Sonraki',
    'product.modal.whatsapp.cta': 'WhatsApp ile Teklif Al',
    'product.modal.share': 'Paylaş',
    'aria.viewMode': 'Görünüm modu',
    'whatsapp.default': 'Merhaba! ESF Otomotiv hakkında bilgi almak istiyorum.'
    ,
    'gallery.empty': 'Bu kategoride galeri görseli bulunamadı.'
  },
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.products': 'Products',
    'nav.categories': 'Categories',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    'nav.services': 'Services',
    'nav.references': 'References',

    // Header CTA
    'header.cta': 'Contact',
    'header.menuToggle': 'Open/close menu',

    // Hero
    'hero.title': 'Leader in Engine Reconditioning Technology',
    'hero.highlight': 'Leader',
    'hero.subtitle': 'With 25 years of experience, we are the trusted partner of the automotive industry. Quality equipment, professional service.',
    'hero.btn.contact': 'Contact Us Now',
    'hero.btn.products': 'Explore Our Products',
    'hero.scroll': 'Scroll Down',
    'hero.tagline': 'Engine Rebuilding Specialist',

    // About
    'about.subtitle': 'About Us',
    'about.title': 'Modern Production Technology and Quality',
    'about.header.desc': "Since 1998, ESF OTOMOTİV has been one of Turkey's leading companies in engine reconditioning equipment and machinery.",
    'about.experience': 'Modern Production Technology',
    'about.story': 'Our Company Story',
    'about.values': 'Our Values',
    'about.values.reliability': 'Reliability',
    'about.values.innovation': 'Innovation',
    'about.values.customer': 'Customer Focus',
    'about.vision': 'Our Vision',
    'about.mission': 'Our Mission',
    'about.timeline': 'Our Timeline',

    // References
    'references.subtitle': 'Trusted Partners',
    'references.title': 'Our References',
    'references.desc': 'Valued partners we have worked with over 25 years and the quality services we provided to them.',
    'references.since': 'Collaboration Start',
    'references.years': 'Years of Collaboration',
    'references.projects': 'Projects',
    'references.ach.partners': '50+ Partners',
    'references.ach.partners.desc': 'Reliable and long-term collaborations',
    'references.ach.satisfaction': '98% Customer Satisfaction',
    'references.ach.satisfaction.desc': 'Quality service and trusted solutions',
    'references.ach.nationwide': 'Nationwide',
    'references.ach.nationwide.desc': 'Service network across the country',

    // Contact
    'contact.map': 'Location Map',
    'contact.subtitle': 'Contact',
    'contact.title': 'Get In Touch',
    'contact.description': 'We’re happy to help. Reach out with any questions.',
    'contact.form.title': 'Contact Form',
    'contact.form.subtitle': 'You can contact us by filling out the form',
    'contact.submit': 'Send Message',
    'contact.info.title': 'Contact Information',
    'contact.info.address': 'Address',
    'contact.info.phone': 'Phone',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.email': 'E-mail',
    'contact.info.hours': 'Working Hours',
    'contact.map.open': 'Open in Maps',
    'contact.labels.name': 'Full Name *',
    'contact.labels.email': 'Email *',
    'contact.labels.phone': 'Phone',
    'contact.labels.company': 'Company',
    'contact.labels.subject': 'Subject *',
    'contact.labels.message': 'Message *',

    // Products
    'products.subtitle': 'Our Products',
    'products.title': 'Our Wide Product Range',
    'products.desc': 'All equipment and machinery you need for engine reconditioning',
    'products.viewGallery': 'Product Gallery',
    'products.unit': 'product',
    'products.moreCategories': 'more categories',
    'products.gallery.image': 'Image',
    'products.gallery.detail': 'Detail',
    // Products: Top-level categories
    'products.categories.machines.title': 'Engine Rebuilding Machines',
    'products.categories.machines.desc': 'Industrial-grade engine rebuilding machines',
    'products.categories.spare-parts.title': 'Engine Rebuilding Spare Parts',
    'products.categories.spare-parts.desc': 'Original spare parts and maintenance kits',
    'products.categories.service-equipment.title': 'Automotive Service Equipment',
    'products.categories.service-equipment.desc': 'Professional service tools and equipment',
    'products.categories.second-hand.title': 'Second-Hand Products for Sale',
    'products.categories.second-hand.desc': 'Tested and guaranteed second-hand equipment',

    // Services
    'services.subtitle': 'Our Services',
    'services.title': 'Comprehensive Service Network',
    'services.desc': 'We are with you with the quality services we provide',
    'services.items.tech-consulting.title': 'Technical Consulting',
    'services.items.tech-consulting.desc': 'We provide 24/7 technical support and consulting with our expert team.',
    'services.items.fast-delivery.title': 'Fast Delivery',
    'services.items.fast-delivery.desc': 'We deliver your orders via a fast and secure network across Turkey.',
    'services.items.installation.title': 'Installation and Setup',
    'services.items.installation.desc': 'We provide professional installation and setup services for purchased machines.',
    'services.items.warranty.title': 'Warranty and Service',
    'services.items.warranty.desc': 'Comprehensive warranty and after-sales service support for all our products.',
    'services.items.training.title': 'Training Services',
    'services.items.training.desc': 'Staff training and certification programs for equipment usage.',
    'services.items.custom-solutions.title': 'Custom Solutions',
    'services.items.custom-solutions.desc': 'We develop custom design and production solutions tailored to your needs.',

    // Hero stats
    'hero.stats.experience': 'Transition and Production',
    'hero.stats.happyCustomers': 'Happy Customers',
    'hero.stats.productVariety': 'Product Variety',
    'hero.stats.techSupport': 'Technical Support',

    // About extra texts
    'about.experience.desc': 'Transformation from 2017 to 2022: With a CNC machine park and expert team, we strengthened production with modern technology and deliver to domestic and global markets with robust quality processes.',
    'about.story.text1': 'ESF OTOMOTİV was founded in 1998 to provide services in the field of engine reconditioning technologies.',
    'about.story.text2': 'With 25+ years of experience, we offer quality products to customers across Turkey.',
    'about.values.reliability.desc': 'The trust we build with our customers is our greatest value.',
    'about.values.innovation.desc': 'We continuously innovate and improve by following technological advancements.',
    'about.values.customer.desc': 'We understand customer needs and offer the most suitable solutions.',
    'about.vision.desc': 'To be Turkey’s leading company in engine reconditioning technologies and to hold a strong position in international markets.',
    'about.mission.desc': 'To contribute to our customers’ success by providing high-quality engine reconditioning equipment and machinery to the automotive sector.',
    'about.timeline.2017.title': 'Start of Marketing',
    'about.timeline.2017.desc': 'Operations began with the marketing of engine reconditioning equipment.',
    'about.timeline.2022.title': 'Shift to Manufacturing',
    'about.timeline.2022.desc': 'Transitioned to a production-focused structure based on industry demand.',
    'about.timeline.today.title': 'Today',
    'about.timeline.today.desc': 'With a CNC park and quality processes, we produce for domestic and global markets.',

    // About features (cards)
    'about.features.0.title': 'CNC Production Infrastructure',
    'about.features.0.desc': 'Modern CNC park and expert technical team',
    'about.features.1.title': 'Wide Product Range',
    'about.features.1.desc': '500+ different products and solution options',
    'about.features.2.title': 'Nationwide Service',
    'about.features.2.desc': 'Fast delivery and technical support network',
    'about.features.3.title': 'Quality Assurance',
    'about.features.3.desc': 'International quality standards across all products',

    // Contact errors & success
    'contact.success': 'Your message has been sent successfully. We will get back to you shortly.',
    'contact.errors.nameRequired': 'Full name is required',
    'contact.errors.emailRequired': 'Email is required',
    'contact.errors.emailInvalid': 'Please enter a valid email address',
    'contact.errors.subjectRequired': 'Subject is required',
    'contact.errors.messageRequired': 'Message is required',
    // Company Working Hours
    'company.hours.weekdays': 'Mon - Fri: 08:00 - 18:00',
    'company.hours.saturday': 'Saturday: 09:00 - 16:00',
    'company.hours.sunday': 'Sunday: Closed',
    
    // Footer UI
    'footer.quickLinks': 'Quick Links',
    'footer.description': 'With 25 years of experience in engine reconditioning, we are the trusted partner of the automotive industry. Quality equipment, professional service, and a customer-focused approach.',
    'footer.copyright': 'All rights reserved.',
    'footer.legal.privacy': 'Privacy',
    'footer.legal.terms': 'Terms',
    'footer.legal.cookies': 'Cookies',
    'footer.developer.by': 'Developed by',

    // Products - Additional top-level categories (name-based ones)
    'products.categories.motor-yenileme.title': 'Engine Rebuilding Machines',
    'products.categories.motor-yenileme.description': 'Engine rebuilding and overhaul machines',
    'products.categories.test-olcu.title': 'Test and Measurement Equipment',
    'products.categories.test-olcu.description': 'Engine testing and measurement equipment',
    'products.categories.polisaj-yikama.title': 'Washing and Polishing Machines',
    'products.categories.polisaj-yikama.description': 'Industrial washing and polishing machines',
    'products.categories.subap-taslama.title': 'Valve and Cutter Grinding',
    'products.categories.subap-taslama.description': 'Valve grinding and cutter group equipment',
    'products.filter.all': 'All',
    'products.categoryGallery': 'Category Gallery',
    'products.search.placeholder': 'Search by product name or description',
    'sort.recent': 'Newest',
    'sort.name': 'By name (A-Z)',
    'sort.category': 'By category',
    'view.grid': 'Grid',
    'view.list': 'List',
    'results.label': 'results',
    'common.detail': 'Detail',
    'whatsapp.messageWithProduct': 'Hello, I would like info and a quote about {product}.',
    'products.empty.title': 'No products in the catalog yet',
    'products.empty.desc': 'Contact our representative to quickly list products that fit your needs.',
    'contact.whatsapp.cta': 'Contact via WhatsApp',
    'contact.form.cta': 'Contact Form',
    'common.loadMore': 'Load more',
    'ui.copyLinkSuccess': 'Link copied',
    'common.close': 'Close',
    'common.previous': 'Previous',
    'common.next': 'Next',
    'product.modal.whatsapp.cta': 'Get Quote via WhatsApp',
    'product.modal.share': 'Share',
    'aria.viewMode': 'View mode',
    'whatsapp.default': "Hello! I'd like information about ESF Otomotiv."
    ,
    'gallery.empty': 'No gallery images found for this category.'
  },
  it: {
    'nav.home': 'Home',
    'nav.products': 'Prodotti',
    'nav.categories': 'Categorie',
    'nav.about': 'Chi Siamo',
    'nav.contact': 'Contatti',
    'nav.services': 'Servizi',
    'nav.references': 'Referenze',

    'header.cta': 'Contatti',
    'header.menuToggle': 'Apri/chiudi menu',

    'hero.title': 'Leader nella tecnologia di revisione motori',
    'hero.highlight': 'Leader',
    'hero.subtitle': 'Con 25 anni di esperienza, siamo un partner affidabile per l’industria automobilistica. Attrezzature di qualità e servizio professionale.',
    'hero.btn.contact': 'Contattaci Ora',
    'hero.btn.products': 'Esplora i Prodotti',
    'hero.scroll': 'Scorri in basso',
    'hero.tagline': 'Specialista nella revisione motori',

    'about.subtitle': 'Chi Siamo',
    'about.title': 'Tecnologia di produzione moderna e qualità',
    'about.header.desc': "Dal 1998, ESF OTOMOTİV è tra le aziende leader in Turchia per attrezzature e macchinari di revisione motori.",
    'about.experience': 'Tecnologia di produzione moderna',
    'about.story': 'La nostra storia',
    'about.values': 'I nostri valori',
    'about.values.reliability': 'Affidabilità',
    'about.values.innovation': 'Innovazione',
    'about.values.customer': 'Orientamento al cliente',
    'about.vision': 'La nostra visione',
    'about.mission': 'La nostra missione',
    'about.timeline': 'La nostra timeline',

    'references.subtitle': 'Partner di fiducia',
    'references.title': 'Le nostre referenze',
    'references.desc': 'Partner di valore con cui abbiamo collaborato in 25 anni e i servizi di qualità offerti.',
    'references.since': 'Inizio collaborazione',
    'references.years': 'Anni di collaborazione',
    'references.projects': 'Progetti',
    'references.ach.partners': '50+ Partner',
    'references.ach.partners.desc': 'Collaborazioni affidabili e durature',
    'references.ach.satisfaction': '98% Soddisfazione Clienti',
    'references.ach.satisfaction.desc': 'Servizio di qualità e soluzioni affidabili',
    'references.ach.nationwide': 'In tutta la nazione',
    'references.ach.nationwide.desc': 'Rete di servizi su tutto il territorio',

    'contact.map': 'Mappa della posizione',
    'contact.subtitle': 'Contatti',
    'contact.title': 'Mettiti in contatto',
    'contact.description': 'Siamo felici di aiutarti. Contattaci per qualsiasi domanda.',
    'contact.form.title': 'Modulo di contatto',
    'contact.form.subtitle': 'Puoi contattarci compilando il modulo',
    'contact.submit': 'Invia messaggio',
    'contact.info.title': 'Informazioni di contatto',
    'contact.info.address': 'Indirizzo',
    'contact.info.phone': 'Telefono',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.email': 'E-mail',
    'contact.info.hours': 'Orari di lavoro',
    'contact.map.open': 'Apri su Maps',
    'contact.labels.name': 'Nome e Cognome *',
    'contact.labels.email': 'E-mail *',
    'contact.labels.phone': 'Telefono',
    'contact.labels.company': 'Azienda',
    'contact.labels.subject': 'Oggetto *',
    'contact.labels.message': 'Messaggio *',

    'products.subtitle': 'I nostri prodotti',
    'products.title': 'La nostra ampia gamma di prodotti',
    'products.desc': 'Tutta l’attrezzatura e i macchinari di cui hai bisogno per la revisione motori',
    'products.viewGallery': 'Galleria Prodotti',
    'products.unit': 'prodotto',
    'products.moreCategories': 'altre categorie',
    'products.gallery.image': 'Immagine',
    'products.gallery.detail': 'Dettaglio',
    'products.categories.machines.title': 'Macchine per la revisione motori',
    'products.categories.machines.desc': 'Macchine industriali per revisione motori',
    'products.categories.spare-parts.title': 'Ricambi per revisione motori',
    'products.categories.spare-parts.desc': 'Ricambi originali e kit di manutenzione',
    'products.categories.service-equipment.title': 'Attrezzature per officina',
    'products.categories.service-equipment.desc': 'Strumenti e attrezzature professionali',
    'products.categories.second-hand.title': 'Prodotti usati in vendita',
    'products.categories.second-hand.desc': 'Attrezzature usate testate e garantite',

    'services.subtitle': 'I nostri servizi',
    'services.title': 'Rete di servizi completa',
    'services.desc': 'Siamo al tuo fianco con servizi di qualità',
    'services.items.tech-consulting.title': 'Consulenza tecnica',
    'services.items.tech-consulting.desc': 'Supporto tecnico 24/7 con il nostro team di esperti.',
    'services.items.fast-delivery.title': 'Consegna rapida',
    'services.items.fast-delivery.desc': 'Consegna rapida e sicura in tutta la Turchia.',
    'services.items.installation.title': 'Installazione e montaggio',
    'services.items.installation.desc': 'Servizi professionali di installazione per le macchine acquistate.',
    'services.items.warranty.title': 'Garanzia e servizio',
    'services.items.warranty.desc': 'Garanzia completa e assistenza post-vendita per tutti i prodotti.',
    'services.items.training.title': 'Servizi di formazione',
    'services.items.training.desc': 'Formazione del personale e programmi di certificazione.',
    'services.items.custom-solutions.title': 'Soluzioni personalizzate',
    'services.items.custom-solutions.desc': 'Soluzioni su misura per le tue esigenze.',

    'hero.stats.experience': 'Transizione e produzione',
    'hero.stats.happyCustomers': 'Clienti soddisfatti',
    'hero.stats.productVariety': 'Varietà di prodotti',
    'hero.stats.techSupport': 'Supporto tecnico',

    'about.experience.desc': 'Trasformazione dal 2017 al 2022: con parco macchine CNC e team esperto, abbiamo rafforzato la produzione con tecnologia moderna; processi di qualità per mercati nazionali e globali.',
    'about.story.text1': 'ESF OTOMOTİV è stata fondata nel 1998 per offrire servizi nel campo delle tecnologie di revisione motori.',
    'about.story.text2': 'Con oltre 25 anni di esperienza, offriamo prodotti di qualità ai clienti in tutta la Turchia.',
    'about.values.reliability.desc': 'La fiducia costruita con i clienti è il nostro valore più grande.',
    'about.values.innovation.desc': 'Innoviamo e miglioriamo continuamente seguendo gli sviluppi tecnologici.',
    'about.values.customer.desc': 'Comprendiamo le esigenze dei clienti e offriamo soluzioni adeguate.',
    'about.vision.desc': 'Essere leader in Turchia nelle tecnologie di revisione motori e avere una forte posizione nei mercati internazionali.',
    'about.mission.desc': 'Contribuire al successo dei nostri clienti fornendo attrezzature e macchinari di alta qualità per la revisione motori.',
    'about.timeline.2017.title': 'Inizio marketing',
    'about.timeline.2017.desc': 'Le attività sono iniziate con la commercializzazione di attrezzature per revisione motori.',
    'about.timeline.2022.title': 'Passaggio alla produzione',
    'about.timeline.2022.desc': 'Transizione a una struttura orientata alla produzione in base alla domanda del settore.',
    'about.timeline.today.title': 'Oggi',
    'about.timeline.today.desc': 'Con parco CNC e processi di qualità, produciamo per mercati nazionali e globali.',

    'about.features.0.title': 'Infrastruttura di produzione CNC',
    'about.features.0.desc': 'Parco CNC moderno e team tecnico esperto',
    'about.features.1.title': 'Ampia gamma di prodotti',
    'about.features.1.desc': 'Oltre 500 prodotti e soluzioni',
    'about.features.2.title': 'Servizio nazionale',
    'about.features.2.desc': 'Rete di consegna e supporto tecnico rapido',
    'about.features.3.title': 'Garanzia di qualità',
    'about.features.3.desc': 'Standard di qualità internazionali per tutti i prodotti',

    'contact.success': 'Il tuo messaggio è stato inviato con successo. Ti risponderemo al più presto.',
    'contact.errors.nameRequired': 'Il nome completo è obbligatorio',
    'contact.errors.emailRequired': "L'e-mail è obbligatoria",
    'contact.errors.emailInvalid': 'Inserisci un indirizzo e-mail valido',
    'contact.errors.subjectRequired': 'L’oggetto è obbligatorio',
    'contact.errors.messageRequired': 'Il messaggio è obbligatorio',
    'company.hours.weekdays': 'Lun - Ven: 08:00 - 18:00',
    'company.hours.saturday': 'Sabato: 09:00 - 16:00',
    'company.hours.sunday': 'Domenica: Chiuso',
    'footer.quickLinks': 'Link Rapidi',
    'footer.description': 'Con 25 anni di esperienza nella revisione motori, siamo un partner affidabile per l’industria automobilistica. Attrezzature di qualità, servizio professionale e approccio orientato al cliente.',
    'footer.copyright': 'Tutti i diritti riservati.',
    'footer.legal.privacy': 'Privacy',
    'footer.legal.terms': 'Termini',
    'footer.legal.cookies': 'Cookie',
    'footer.developer.by': 'Sviluppato da',

    'products.categories.motor-yenileme.title': 'Macchine per revisione motori',
    'products.categories.motor-yenileme.description': 'Macchine per revisione e revisione motori',
    'products.categories.test-olcu.title': 'Attrezzature di test e misura',
    'products.categories.test-olcu.description': 'Attrezzature per test e misurazione del motore',
    'products.categories.polisaj-yikama.title': 'Macchine di lavaggio e lucidatura',
    'products.categories.polisaj-yikama.description': 'Macchine industriali di lavaggio e lucidatura',
    'products.categories.subap-taslama.title': 'Rettifica valvole e utensili',
    'products.categories.subap-taslama.description': 'Attrezzature per la rettifica valvole e gruppi di taglio',
    'products.filter.all': 'Tutti',
    'products.categoryGallery': 'Galleria Categorie',
    'products.search.placeholder': 'Cerca per nome prodotto o descrizione',
    'sort.recent': 'Più recente',
    'sort.name': 'Per nome (A-Z)',
    'sort.category': 'Per categoria',
    'view.grid': 'Griglia',
    'view.list': 'Lista',
    'results.label': 'risultati',
    'common.detail': 'Dettaglio',
    'whatsapp.messageWithProduct': 'Ciao, vorrei informazioni e un preventivo su {product}.',
    'products.empty.title': 'Non ci sono ancora prodotti nel catalogo',
    'products.empty.desc': 'Contatta il nostro referente per elencare rapidamente i prodotti adatti alle tue esigenze.',
    'contact.whatsapp.cta': 'Contatta via WhatsApp',
    'contact.form.cta': 'Modulo di contatto',
    'common.loadMore': 'Carica altro',
    'ui.copyLinkSuccess': 'Link copiato',
    'common.close': 'Chiudi',
    'common.previous': 'Precedente',
    'common.next': 'Successivo',
    'product.modal.whatsapp.cta': 'Richiedi un preventivo via WhatsApp',
    'product.modal.share': 'Condividi',
    'aria.viewMode': 'Modalità di visualizzazione',
    'whatsapp.default': 'Ciao! Vorrei informazioni su ESF Otomotiv.'
    ,
    'gallery.empty': 'Nessuna immagine di galleria trovata per questa categoria.'
  },
  ru: {
    'nav.home': 'Главная',
    'nav.products': 'Продукция',
    'nav.categories': 'Категории',
    'nav.about': 'О компании',
    'nav.contact': 'Контакты',
    'nav.services': 'Услуги',
    'nav.references': 'Референсы',

    'header.cta': 'Связаться',
    'header.menuToggle': 'Открыть/закрыть меню',

    'hero.title': 'Лидер в технологии восстановления двигателей',
    'hero.highlight': 'Лидер',
    'hero.subtitle': 'С 25-летним опытом мы — надежный партнёр автопрома. Качественное оборудование и профессиональный сервис.',
    'hero.btn.contact': 'Связаться сейчас',
    'hero.btn.products': 'Посмотреть продукцию',
    'hero.scroll': 'Прокрутить вниз',
    'hero.tagline': 'Специалист по восстановлению двигателей',

    'about.subtitle': 'О нас',
    'about.title': 'Современные технологии производства и качество',
    'about.header.desc': 'С 1998 года ESF OTOMOTİV — одна из ведущих компаний Турции в сфере оборудования и станков для восстановления двигателей.',
    'about.experience': 'Современные технологии производства',
    'about.story': 'История компании',
    'about.values': 'Наши ценности',
    'about.values.reliability': 'Надёжность',
    'about.values.innovation': 'Инновации',
    'about.values.customer': 'Ориентация на клиента',
    'about.vision': 'Наше видение',
    'about.mission': 'Наша миссия',
    'about.timeline': 'Хронология',

    'references.subtitle': 'Надёжные партнёры',
    'references.title': 'Наши референсы',
    'references.desc': 'Ценные партнёры, с которыми мы сотрудничали за 25 лет, и предоставленные им качественные услуги.',
    'references.since': 'Начало сотрудничества',
    'references.years': 'Лет сотрудничества',
    'references.projects': 'Проекты',
    'references.ach.partners': '50+ партнёров',
    'references.ach.partners.desc': 'Надёжные и долгосрочные сотрудничества',
    'references.ach.satisfaction': '98% удовлетворённости клиентов',
    'references.ach.satisfaction.desc': 'Качественный сервис и надёжные решения',
    'references.ach.nationwide': 'По всей стране',
    'references.ach.nationwide.desc': 'Сеть обслуживания по всей стране',

    'contact.map': 'Карта расположения',
    'contact.subtitle': 'Контакты',
    'contact.title': 'Свяжитесь с нами',
    'contact.description': 'Мы рады помочь. Свяжитесь с нами по любым вопросам.',
    'contact.form.title': 'Форма обратной связи',
    'contact.form.subtitle': 'Заполните форму, чтобы связаться с нами',
    'contact.submit': 'Отправить сообщение',
    'contact.info.title': 'Контактная информация',
    'contact.info.address': 'Адрес',
    'contact.info.phone': 'Телефон',
    'contact.info.whatsapp': 'WhatsApp',
    'contact.info.email': 'E-mail',
    'contact.info.hours': 'Время работы',
    'contact.map.open': 'Открыть на карте',
    'contact.labels.name': 'ФИО *',
    'contact.labels.email': 'E-mail *',
    'contact.labels.phone': 'Телефон',
    'contact.labels.company': 'Компания',
    'contact.labels.subject': 'Тема *',
    'contact.labels.message': 'Сообщение *',

    'products.subtitle': 'Наша продукция',
    'products.title': 'Широкая линейка продукции',
    'products.desc': 'Всё оборудование и станки, необходимые для восстановления двигателей',
    'products.viewGallery': 'Галерея продукции',
    'products.unit': 'продукт',
    'products.moreCategories': 'ещё категорий',
    'products.gallery.image': 'Изображение',
    'products.gallery.detail': 'Детали',

    'products.categories.machines.title': 'Станки для восстановления двигателей',
    'products.categories.machines.desc': 'Промышленные станки для восстановления двигателей',
    'products.categories.spare-parts.title': 'Запчасти для восстановления двигателей',
    'products.categories.spare-parts.desc': 'Оригинальные запчасти и комплекты для обслуживания',
    'products.categories.service-equipment.title': 'Оборудование для автосервиса',
    'products.categories.service-equipment.desc': 'Профессиональные инструменты и оборудование',
    'products.categories.second-hand.title': 'Б/у продукция на продажу',
    'products.categories.second-hand.desc': 'Проверенное и гарантированное б/у оборудование',

    'services.subtitle': 'Наши услуги',
    'services.title': 'Комплексная сеть обслуживания',
    'services.desc': 'Мы рядом с вами, предоставляя качественные услуги',
    'services.items.tech-consulting.title': 'Техническая консультация',
    'services.items.tech-consulting.desc': 'Мы предоставляем круглосуточную техническую поддержку и консультации.',
    'services.items.fast-delivery.title': 'Быстрая доставка',
    'services.items.fast-delivery.desc': 'Быстрая и надёжная доставка по всей Турции.',
    'services.items.installation.title': 'Установка и монтаж',
    'services.items.installation.desc': 'Профессиональная установка приобретённых станков.',
    'services.items.warranty.title': 'Гарантия и сервис',
    'services.items.warranty.desc': 'Всесторонняя гарантия и послепродажное обслуживание для всех продуктов.',
    'services.items.training.title': 'Обучение',
    'services.items.training.desc': 'Обучение персонала и программы сертификации по использованию оборудования.',
    'services.items.custom-solutions.title': 'Индивидуальные решения',
    'services.items.custom-solutions.desc': 'Разрабатываем решения, адаптированные под ваши нужды.',

    'hero.stats.experience': 'Переход и производство',
    'hero.stats.happyCustomers': 'Довольные клиенты',
    'hero.stats.productVariety': 'Разнообразие продукции',
    'hero.stats.techSupport': 'Техническая поддержка',

    'about.experience.desc': 'Трансформация с 2017 по 2022: благодаря парку станков ЧПУ и команде экспертов мы усилили производство современной технологией; с качественными процессами работаем для внутреннего и глобального рынков.',
    'about.story.text1': 'ESF OTOMOTİV была основана в 1998 году для работы в сфере технологий восстановления двигателей.',
    'about.story.text2': 'Имея более 25 лет опыта, мы предлагаем качественные продукты клиентам по всей Турции.',
    'about.values.reliability.desc': 'Доверие клиентов — наша главная ценность.',
    'about.values.innovation.desc': 'Мы постоянно внедряем инновации, следуя технологическим тенденциям.',
    'about.values.customer.desc': 'Мы понимаем потребности клиентов и предлагаем наиболее подходящие решения.',
    'about.vision.desc': 'Быть лидером Турции в технологиях восстановления двигателей и иметь сильные позиции на международных рынках.',
    'about.mission.desc': 'Содействовать успеху клиентов, поставляя высококачественное оборудование и станки для восстановления двигателей.',
    'about.timeline.2017.title': 'Начало маркетинга',
    'about.timeline.2017.desc': 'Деятельность началась с маркетинга оборудования для восстановления двигателей.',
    'about.timeline.2022.title': 'Переход к производству',
    'about.timeline.2022.desc': 'Переход к структуре, ориентированной на производство, в соответствии с потребностями отрасли.',
    'about.timeline.today.title': 'Сегодня',
    'about.timeline.today.desc': 'С парком ЧПУ и качественными процессами производим для внутреннего и мирового рынков.',

    'about.features.0.title': 'Инфраструктура производства ЧПУ',
    'about.features.0.desc': 'Современный парк ЧПУ и команда экспертов',
    'about.features.1.title': 'Широкий ассортимент',
    'about.features.1.desc': 'Более 500 продуктов и решений',
    'about.features.2.title': 'Сервис по всей стране',
    'about.features.2.desc': 'Быстрая доставка и сеть техподдержки',
    'about.features.3.title': 'Гарантия качества',
    'about.features.3.desc': 'Международные стандарты качества для всех продуктов',

    'contact.success': 'Ваше сообщение успешно отправлено. Мы скоро свяжемся с вами.',
    'contact.errors.nameRequired': 'ФИО обязательно',
    'contact.errors.emailRequired': 'E-mail обязателен',
    'contact.errors.emailInvalid': 'Введите корректный адрес e-mail',
    'contact.errors.subjectRequired': 'Тема обязательна',
    'contact.errors.messageRequired': 'Сообщение обязательно',

    'company.hours.weekdays': 'Пн - Пт: 08:00 - 18:00',
    'company.hours.saturday': 'Суббота: 09:00 - 16:00',
    'company.hours.sunday': 'Воскресенье: Закрыто',

    'footer.quickLinks': 'Быстрые ссылки',
    'footer.description': 'Имея 25 лет опыта в восстановлении двигателей, мы — надёжный партнёр автопрома. Качественное оборудование, профессиональный сервис и клиентоориентированный подход.',
    'footer.copyright': 'Все права защищены.',
    'footer.legal.privacy': 'Конфиденциальность',
    'footer.legal.terms': 'Условия',
    'footer.legal.cookies': 'Cookies',
    'footer.developer.by': 'Разработчик',

    'products.categories.motor-yenileme.title': 'Станки для восстановления двигателей',
    'products.categories.motor-yenileme.description': 'Станки для восстановления и ревизии двигателя',
    'products.categories.test-olcu.title': 'Оборудование для тестов и измерений',
    'products.categories.test-olcu.description': 'Оборудование для тестирования и измерения двигателя',
    'products.categories.polisaj-yikama.title': 'Станки для мойки и полировки',
    'products.categories.polisaj-yikama.description': 'Промышленные станки для мойки и полировки',
    'products.categories.subap-taslama.title': 'Шлифовка клапанов и инструмента',
    'products.categories.subap-taslama.description': 'Оборудование для шлифовки клапанов и режущих групп',

    'products.filter.all': 'Все',
    'products.categoryGallery': 'Галерея категорий',
    'products.search.placeholder': 'Поиск по названию или описанию',
    'sort.recent': 'Самые новые',
    'sort.name': 'По имени (A–Z)',
    'sort.category': 'По категории',
    'view.grid': 'Сетка',
    'view.list': 'Список',
    'results.label': 'результатов',
    'common.detail': 'Детали',
    'whatsapp.messageWithProduct': 'Здравствуйте, хотел бы получить информацию и цену по {product}.',
    'products.empty.title': 'В каталоге пока нет продуктов',
    'products.empty.desc': 'Свяжитесь с нашим представителем, чтобы быстро подобрать продукцию под ваши нужды.',
    'contact.whatsapp.cta': 'Связаться по WhatsApp',
    'contact.form.cta': 'Форма контакта',
    'common.loadMore': 'Загрузить ещё',
    'ui.copyLinkSuccess': 'Ссылка скопирована',
    'common.close': 'Закрыть',
    'common.previous': 'Предыдущий',
    'common.next': 'Следующий',
    'product.modal.whatsapp.cta': 'Получить предложение в WhatsApp',
    'product.modal.share': 'Поделиться',
    'aria.viewMode': 'Режим просмотра',
    'whatsapp.default': 'Здравствуйте! Хотел бы получить информацию об ESF Otomotiv.'
    ,
    'gallery.empty': 'Для этой категории изображения галереи не найдены.'
  },
};

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguageState] = useState<SupportedLanguage>(() => {
    const saved = localStorage.getItem('esf-language');
    return saved === 'en' || saved === 'it' || saved === 'ru' ? (saved as SupportedLanguage) : 'tr';
  });

  const setLanguage = (lang: SupportedLanguage) => {
    setLanguageState(lang);
    localStorage.setItem('esf-language', lang);
    document.documentElement.setAttribute('lang', lang);
  };

  useEffect(() => {
    document.documentElement.setAttribute('lang', language);
  }, [language]);

  const t = useMemo(() => {
    return (key: string) => {
      const dict = translations[language];
      return dict[key] ?? translations.tr[key] ?? key;
    };
  }, [language]);

  const value = useMemo(() => ({ language, setLanguage, t }), [language, t]);

  return (
    <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
};
